import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faChevronRight, faPlus, faTrash, faPencilAlt,
  faTimes, faExchangeAlt, faSpinner, faShield,
} from '@fortawesome/free-solid-svg-icons';
import * as FaIcons from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';

// Curated icon palette — same set we already use elsewhere in the app.
const ICON_PALETTE = [
  'fire', 'bolt', 'hard-hat', 'wrench', 'cogs', 'user-shield',
  'exclamation-triangle', 'clipboard-check', 'search', 'exclamation-circle',
  'tint', 'biohazard', 'drafting-compass', 'chart-bar', 'folder-open',
  'shield-alt', 'building', 'key', 'gavel', 'fire-extinguisher',
];
// Curated colour palette derived from our design tokens.
const COLOUR_PALETTE = [
  '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#6366f1',
  '#14b8a6', '#e11d48', '#0ea5e9', '#84cc16', '#f97316',
  '#dc2626', '#06b6d4', '#a855f7', '#1A80E5', '#7c3aed',
  '#22c55e', '#6b7280',
];

// Map FontAwesome icon name strings → imported icon objects.
const iconForName = (name) => {
  if (!name) return faShield;
  const studly = 'fa' + name.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
  return FaIcons[studly] || faShield;
};

// P1-25: managed document categories — collapsible CRUD on the Documents
// page, plus per-category bulk recategorise.
const DocumentsCategoriesAdmin = ({
  categories,
  onChange,
  documents,
  propertyId,
  canManage,
  onBulkRecategorised,
}) => {
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [recategorising, setRecategorising] = useState(null); // a category row
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', icon: 'folder-open', colour: '#6b7280' });

  const startAdd = () => {
    setForm({ name: '', icon: 'folder-open', colour: '#6b7280' });
    setEditing(null);
    setShowAdd(true);
  };
  const startEdit = (cat) => {
    setForm({ name: cat.name, icon: cat.icon || 'folder-open', colour: cat.colour || '#6b7280' });
    setEditing(cat);
    setShowAdd(true);
  };

  const closeModal = () => { setShowAdd(false); setEditing(null); };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error('Category name is required'); return; }
    setBusy(true);
    try {
      const isEdit = !!editing;
      const url = isEdit
        ? getApiEndpoint(`/api/document-categories/${editing.id}`)
        : getApiEndpoint('/api/document-categories');
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), icon: form.icon, colour: form.colour }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'Failed');
      toast.success(isEdit ? 'Category updated' : 'Category added');
      closeModal();
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'Failed to save category');
    } finally { setBusy(false); }
  };

  const remove = async (cat) => {
    if (cat.is_system) { toast.error('System categories cannot be deleted'); return; }
    if (!window.confirm(`Delete "${cat.name}"? Documents in this category will move to "Other".`)) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/document-categories/${cat.id}`), {
        method: 'DELETE', headers: getAuthHeader(),
      });
      if (!r.ok) throw new Error();
      toast.success('Category deleted');
      onChange?.();
    } catch (_) { toast.error('Failed to delete category'); }
  };

  const docsInCategory = useCallback((cat) => {
    return (documents || []).filter(d =>
      (cat.id && d.category_id === cat.id) || d.type === cat.name
    );
  }, [documents]);

  return (
    <section className={`docs-cats-admin ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="docs-cats-admin-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="docs-cats-admin-chev" />
        <span className="docs-cats-admin-title">Manage categories</span>
        <span className="docs-cats-admin-count">{categories?.length || 0}</span>
      </button>

      {open && (
        <div className="docs-cats-admin-body">
          {canManage && (
            <button type="button" className="docs-cats-admin-add" onClick={startAdd}>
              <FontAwesomeIcon icon={faPlus} /> Add category
            </button>
          )}
          <ul className="docs-cats-admin-list">
            {(categories || []).map(cat => (
              <li key={cat.id} className="docs-cats-admin-row" style={{ '--cat-color': cat.colour || '#6b7280' }}>
                <span className="docs-cats-admin-icon">
                  <FontAwesomeIcon icon={iconForName(cat.icon)} />
                </span>
                <span className="docs-cats-admin-name">
                  {cat.name}
                  {cat.is_system && <span className="docs-cats-admin-system">system</span>}
                </span>
                <span className="docs-cats-admin-doc-count">
                  {Number(cat.document_count) || 0} doc{Number(cat.document_count) === 1 ? '' : 's'}
                </span>
                {Number(cat.document_count) > 0 && (
                  <button
                    type="button"
                    className="docs-cats-admin-recat"
                    onClick={() => setRecategorising(cat)}
                    title="Move documents from this category"
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} /> Re-categorise
                  </button>
                )}
                {canManage && (
                  <>
                    <button
                      type="button"
                      className="docs-cats-admin-edit"
                      onClick={() => startEdit(cat)}
                      title={cat.is_system ? 'Edit colour & icon' : 'Edit'}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                    {!cat.is_system && (
                      <button
                        type="button"
                        className="docs-cats-admin-del"
                        onClick={() => remove(cat)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add / edit modal */}
      {showAdd && (
        <div className="docs-modal-overlay" onClick={closeModal}>
          <form className="docs-cat-modal" onClick={e => e.stopPropagation()} onSubmit={save}>
            <header className="docs-cat-modal-head">
              <h3>{editing ? 'Edit category' : 'New category'}</h3>
              <button type="button" className="docs-modal-close" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </header>
            <div className="docs-cat-modal-body">
              <label className="docs-cat-field">
                <span>Name</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Insurance"
                  required
                  disabled={!!editing && editing.is_system}
                />
                {editing?.is_system && <small>System categories can&apos;t be renamed.</small>}
              </label>
              <label className="docs-cat-field">
                <span>Colour</span>
                <div className="docs-cat-swatches">
                  {COLOUR_PALETTE.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`docs-cat-swatch ${form.colour === c ? 'selected' : ''}`}
                      style={{ background: c }}
                      onClick={() => setForm(f => ({ ...f, colour: c }))}
                      aria-label={`Pick ${c}`}
                    />
                  ))}
                </div>
              </label>
              <label className="docs-cat-field">
                <span>Icon</span>
                <div className="docs-cat-icon-grid">
                  {ICON_PALETTE.map(name => (
                    <button
                      key={name}
                      type="button"
                      className={`docs-cat-icon-pick ${form.icon === name ? 'selected' : ''}`}
                      onClick={() => setForm(f => ({ ...f, icon: name }))}
                      aria-label={name}
                    >
                      <FontAwesomeIcon icon={iconForName(name)} />
                    </button>
                  ))}
                </div>
              </label>
              <div className="docs-cat-preview">
                <span className="docs-cat-preview-label">Preview</span>
                <span className="docs-cat-preview-chip" style={{ '--cat-color': form.colour }}>
                  <FontAwesomeIcon icon={iconForName(form.icon)} />
                  {form.name || 'New category'}
                </span>
              </div>
            </div>
            <footer className="docs-cat-modal-foot">
              <button type="button" className="docs-btn-cancel" onClick={closeModal}>Cancel</button>
              <button type="submit" className="docs-btn-submit" disabled={busy}>
                {busy ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : (editing ? 'Save changes' : 'Add category')}
              </button>
            </footer>
          </form>
        </div>
      )}

      {/* Bulk recategorise picker */}
      {recategorising && (
        <BulkRecategoriseModal
          fromCategory={recategorising}
          allCategories={categories}
          documents={docsInCategory(recategorising)}
          propertyId={propertyId}
          onClose={() => setRecategorising(null)}
          onDone={() => { setRecategorising(null); onChange?.(); onBulkRecategorised?.(); }}
        />
      )}
    </section>
  );
};

const BulkRecategoriseModal = ({ fromCategory, allCategories, documents, propertyId, onClose, onDone }) => {
  const [selected, setSelected] = useState(new Set(documents.map(d => d.id)));
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async (e) => {
    e?.preventDefault();
    if (!target) { toast.error('Pick a destination category'); return; }
    if (selected.size === 0) { toast.error('Select at least one document'); return; }
    const dest = allCategories.find(c => String(c.id) === String(target));
    if (!dest) return;
    setBusy(true);
    try {
      const r = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/documents/bulk-category`),
        {
          method: 'PATCH',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ids: Array.from(selected),
            category_id: dest.id,
            type: dest.name,
          }),
        },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'Failed');
      toast.success(`Moved ${data.updated || selected.size} doc${(data.updated || selected.size) === 1 ? '' : 's'} to ${dest.name}`);
      onDone?.();
    } catch (err) {
      toast.error(err.message || 'Failed to recategorise');
    } finally { setBusy(false); }
  };

  return (
    <div className="docs-modal-overlay" onClick={onClose}>
      <form className="docs-cat-modal docs-cat-modal-wide" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <header className="docs-cat-modal-head">
          <h3>Move documents from &ldquo;{fromCategory.name}&rdquo;</h3>
          <button type="button" className="docs-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </header>
        <div className="docs-cat-modal-body">
          <label className="docs-cat-field">
            <span>Move to</span>
            <select value={target} onChange={e => setTarget(e.target.value)} required>
              <option value="">Pick a category…</option>
              {allCategories
                .filter(c => c.id !== fromCategory.id)
                .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <div className="docs-cat-recat-list">
            <div className="docs-cat-recat-list-head">
              <span>{selected.size} of {documents.length} selected</span>
              <button
                type="button"
                className="docs-cat-recat-toggle-all"
                onClick={() => setSelected(prev => prev.size === documents.length ? new Set() : new Set(documents.map(d => d.id)))}
              >
                {selected.size === documents.length ? 'Clear' : 'Select all'}
              </button>
            </div>
            <ul>
              {documents.map(d => (
                <li key={d.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggle(d.id)}
                    />
                    <span className="docs-cat-recat-name">{d.name}</span>
                    {d.zone && <span className="docs-cat-recat-zone">{d.zone}</span>}
                  </label>
                </li>
              ))}
              {documents.length === 0 && <li className="docs-cat-recat-empty">No documents in this category.</li>}
            </ul>
          </div>
        </div>
        <footer className="docs-cat-modal-foot">
          <button type="button" className="docs-btn-cancel" onClick={onClose}>Cancel</button>
          <button type="submit" className="docs-btn-submit" disabled={busy || selected.size === 0 || !target}>
            {busy ? <><FontAwesomeIcon icon={faSpinner} spin /> Moving…</> : <>Move {selected.size} doc{selected.size === 1 ? '' : 's'}</>}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default DocumentsCategoriesAdmin;
